import { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { TextInput } from "@mantine/core";
import { callFunction } from "../api/client";
import type { Campaign } from "../api/types";
import type { SignedIn } from "../lib/auth";
import { PRESETS, presetIsOn, type Filters } from "../lib/campaignFilters";
import { stageWord } from "../lib/campaign";
import { recentDate, shortDate } from "../lib/format";
import { Badge } from "../ui/Badge";
import { Dialog, useConfirm } from "../ui/Dialog";
import { RecordPage, type RecordAction } from "../ui/RecordPage";
import { CampaignCallNotes } from "../components/CampaignCallNotes";
import { CampaignCriteria } from "../components/CampaignCriteria";
import { CampaignEnrichment } from "../components/CampaignEnrichment";
import { ExportDialog } from "../components/CampaignExport";
import { CampaignFile } from "../components/CampaignFile";
import { CampaignChanges } from "../components/CampaignChanges";
import { CampaignHistory } from "../components/CampaignHistory";
import { CampaignList } from "../components/CampaignList";
import { CampaignMove } from "../components/CampaignMove";
import { DatesDialog } from "../components/CampaignDates";
import { OwnerDialog } from "../components/CampaignOwner";
import { SequenceDialog, SequenceLine } from "../components/CampaignSequence";
import { NextStep, StageFlow } from "../components/CampaignStatus";
import { useCampaign } from "../lib/campaign";

/* A campaign as a page, at /campaigns/<slug>.
 *
 * The header answers where the campaign stands and what to do next: the
 * name, what it is (segment, states, size), the stage track, the engine's
 * one line with the one action that takes the next step, a bar for each
 * piece of running work, and the figures. The tabs below are the list it
 * exists to produce, then its criteria, its enrichment, its call notes and
 * its history. List is first, against the Details-first rule for records,
 * because a campaign's own facts are a handful of settings and its list is
 * the point (James approved, 2026-10-08).
 *
 * It replaces the panel over the campaigns list (CampaignRecord.tsx), which
 * stacked seven sections and fifteen actions in a drawer.
 * docs/plans/campaigns-overhaul.md, The campaign page. */

export function CampaignPage({ session }: { session: SignedIn }) {
  const token = session.idToken;
  const name = decodeURIComponent(useParams().name ?? "");
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const q = useCampaign(token, name);
  const c = q.data ?? null;
  const confirm = useConfirm();
  const [exportOpen, setExportOpen] = useState<null | "all" | "new">(null);
  const [completing, setCompleting] = useState(false);
  const [note, setNote] = useState("");
  const [stepError, setStepError] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);
  const [moveTo, setMoveTo] = useState<string | null>(null);
  const [assigning, setAssigning] = useState(false);
  const [dating, setDating] = useState(false);
  const [sequencing, setSequencing] = useState(false);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["campaign", name] });
    queryClient.invalidateQueries({ queryKey: ["campaigns"] });
    queryClient.invalidateQueries({ queryKey: ["campaign-history", name] });
    queryClient.invalidateQueries({ queryKey: ["campaign-builds", name] });
    queryClient.invalidateQueries({ queryKey: ["campaign-changes", name] });
    queryClient.invalidateQueries({ queryKey: ["my-campaigns"] });
    queryClient.invalidateQueries({ queryKey: ["campaign-file", name] });
    queryClient.invalidateQueries({ queryKey: ["sequence-targets"] });
    queryClient.invalidateQueries({ queryKey: ["enrichment-requests"] });
    queryClient.invalidateQueries({ queryKey: ["enrichment-coming-up"] });
  };
  const call = async (fn: string, args: Record<string, unknown>) => {
    setStepError(null);
    try { await callFunction(token, fn, args); refresh(); }
    catch (e) { setStepError(e instanceof Error ? e.message : String(e)); }
  };

  const updateList = async () => {
    setUpdating(true);
    await call("run_campaign", { p_criteria_set: name });
    setUpdating(false);
  };

  const onStep = (step: string) => {
    if (step === "export") setExportOpen("all");
    else if (step === "export_new") setExportOpen("new");
    else if (step === "take_enrichment") void call("start_enrichment", { p_name: name });
    else if (step === "complete_enrichment") { setNote(""); setCompleting(true); }
    else if (step === "mark_ready") void call("set_campaign_status", { p_name: name, p_status: "ready_to_call" });
    // Start enrichment opens the move dialog with the campaign's choices
    // ticked, so the person sees what starts (engine migration 256).
    else if (step === "start") setMoveTo("enriching");
    // The automatic run again, which the page's state reads (engine migration
    // 266). It queued the manual run until 2026-10-20, so the page kept
    // saying the run failed after the press.
    else if (step === "rerun_web") void call("run_web_enrichment_again", { p_name: name });
  };

  const editPath = `/campaigns/new?edit=${encodeURIComponent(name)}`;
  const actions: RecordAction[] = c ? [
    // An archived campaign is restored before its owner changes, as the
    // engine requires, so it has no Assign.
    ...(c.archived_at ? [] : [{ label: c.owner_id ? "Reassign" : "Assign", onSelect: () => setAssigning(true) }]),
    // The dates matter until calling starts.
    ...(c.archived_at || !["draft", "enriching", "ready_to_call"].includes(c.stage ?? "") ? [] : [
      { label: c.call_on || c.enrich_on ? "Change dates" : "Set dates", onSelect: () => setDating(true) },
    ]),
    // A campaign that is done or archived is in no sequence (engine
    // migration 311).
    ...(c.archived_at || !["draft", "enriching", "ready_to_call", "calling"].includes(c.stage ?? "") ? [] : [
      { label: inSequence(c) ? "Move in its sequence" : "Put in a sequence", onSelect: () => setSequencing(true) },
      // Taking it out can start the next campaign's enrichment, so it says
      // what happens behind it first (engine migration 314).
      ...(inSequence(c) ? [{ label: "Take out of sequence", onSelect: () => confirm.ask({
        title: `Take ${c.label} out of its sequence?`,
        says: ["It stays with its owner, in no sequence.", c.when_out_words].filter(Boolean).join(" "),
        label: "Take out of sequence",
        run: () => callFunction(token, "sequence_campaign", { p_name: name }).then(refresh) }) }] : []),
    ]),
    {
      label: "Edit criteria",
      onSelect: () => (c.stage === "calling"
        ? confirm.ask({ title: `Edit ${c.label}?`, says: "This campaign is being called. Changing its criteria changes the list.", label: "Edit criteria", run: async () => navigate(editPath) })
        : navigate(editPath)),
    },
    { label: "Duplicate", onSelect: () => navigate(`/campaigns/new?copy=${encodeURIComponent(name)}`) },
    c.archived_at
      ? { label: "Restore", onSelect: () => confirm.ask({ title: `Restore ${c.label}?`, says: "It returns to the campaigns list.", label: "Restore",
            run: () => callFunction(token, "archive_criteria_set", { p_name: name, p_archived: false }).then(refresh) }) }
      // Archiving moves the sequence as Abandoned does, so it says what
      // happens to the campaign behind (engine migration 314).
      : { label: "Archive", onSelect: () => confirm.ask({ title: `Archive ${c.label}?`,
            says: ["It leaves the campaigns list and keeps its history.", c.when_done_words].filter(Boolean).join(" "),
            label: "Archive", danger: true,
            run: () => callFunction(token, "archive_criteria_set", { p_name: name, p_archived: true }).then(refresh) }) },
  ] : [];

  const listId = search.get("list") || c?.list_id || null;

  return (
    <>
      <RecordPage
        backLabel="Back to campaigns"
        backTo="/campaigns"
        fromKey="campaigns"
        loading={q.isLoading}
        error={q.isError ? (q.error as Error).message : null}
        notFound={!q.isLoading && !q.isError && !c ? "No campaign has this address." : null}
        title={c?.label ?? name}
        facts={c ? <CampaignFacts c={c} /> : null}
        badges={c?.archived_at ? <Badge tone="quiet">Archived {shortDate(c.archived_at)}</Badge> : null}
        status={c ? (
          <NextStep c={c} onStep={c.archived_at ? null : onStep} error={stepError}
                    exportLine={<CampaignFile token={token} campaign={name} />}
                    onUpdateList={c.archived_at ? null : () => void updateList()} updating={updating} />
        ) : null}
        beforeTabs={c ? (
          <>
            <StageFlow c={c} onMove={c.archived_at ? null : setMoveTo} />
            {moveTo ? <CampaignMove key={moveTo} token={token} c={c} to={moveTo} onClose={() => setMoveTo(null)} onMoved={refresh} /> : null}
          </>
        ) : null}
        actions={actions}
        defaultTab="list"
        loadedAt={q.dataUpdatedAt}
        refreshing={q.isFetching}
        onRefresh={refresh}
        tabs={c ? [
          { key: "list", label: "List", count: c.list_count ?? null,
            content: <CampaignList token={token} c={c} listId={listId} onUpdate={updateList} updating={updating} /> },
          { key: "criteria", label: "Criteria", content: <CampaignCriteria token={token} c={c} editPath={editPath} /> },
          { key: "enrichment", label: "Enrichment", content: <CampaignEnrichment key={c.updated_at ?? ""} token={token} c={c} /> },
          { key: "notes", label: "Call notes", content: <CampaignCallNotes key={c.updated_at ?? ""} token={token} c={c} /> },
          { key: "changes", label: "Changes", content: <CampaignChanges token={token} c={c} /> },
          { key: "history", label: "History", content: <CampaignHistory token={token} c={c} /> },
        ] : [{ key: "list", label: "List", content: null }]}
      />
      {c ? (
        <ExportDialog token={token} c={c} opened={exportOpen !== null} newOnly={exportOpen === "new"}
                      onClose={() => setExportOpen(null)}
                      onQueued={() => refresh()} />
      ) : null}
      <Dialog opened={completing} title="Mark enrichment complete" onClose={() => setCompleting(false)}
              action={{ label: "Complete", onPress: async () => {
                await call("complete_enrichment", { p_name: name, p_note: note.trim() || null });
                setCompleting(false);
              } }}>
        <TextInput label="What did you find?" value={note} onChange={(e) => setNote(e.currentTarget.value)} data-autofocus />
      </Dialog>
      {c && assigning ? (
        <OwnerDialog token={token} c={c} opened onClose={() => setAssigning(false)} onDone={refresh} />
      ) : null}
      {c && dating ? (
        <DatesDialog token={token} c={c} opened onClose={() => setDating(false)} onDone={refresh} />
      ) : null}
      {c && sequencing ? (
        <SequenceDialog token={token} c={c} opened onClose={() => setSequencing(false)} onDone={refresh} />
      ) : null}
      {confirm.dialog}
    </>
  );
}

/** What the campaign is, in one line: its segment, where, and how big, when
 *  it was made, and who owns it. */
function CampaignFacts({ c }: { c: Campaign }) {
  const f = (c.filters ?? {}) as Filters;
  const segment = PRESETS.find((p) => presetIsOn(f, p))?.label;
  const states = Array.isArray(f.state_in) ? (f.state_in as string[]).join(", ") : null;
  const units = typeof f.min_units === "number"
    ? `${f.min_units} ${f.min_units === 1 ? "unit" : "units"} and up` : null;
  // The criteria that say what the campaign is, then when it was made, so
  // the line says more than a state code when a campaign has no segment.
  const parts = [segment, states, units].filter(Boolean);
  return (
    <>
      {parts.length ? parts.join(" · ") : stageWord(c.stage)}
      {c.created_at ? <span className="muted"> · made {recentDate(c.created_at)}</span> : null}
      {c.owner_name ? <> · Owner: {c.owner_name}</> : <span className="muted"> · No owner</span>}
      {inSequence(c) && c.sequence_words ? <> · <SequenceLine c={c} /></> : null}
    </>
  );
}

/** In a sequence of more than itself, by the engine's count (migration 311). */
function inSequence(c: Campaign): boolean {
  return (c.sequence_length ?? 1) > 1;
}

